import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CUSTOMER_STATUSES, entityId, type Customer, type CustomerStatus } from '@oscom/shared';
import { ApiRequestError } from '../api/client';
import { getCustomer, saveCustomer } from '../api/customers';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { Layout } from '../components/Layout';
import { CUSTOMER_STATUS_LABELS } from './customerStatusLabels';

/** The `:customerId` value that means "create a new customer here". */
const NEW_CUSTOMER_ID = 'neu';

/**
 * Customer detail / create form.
 *
 * Editable fields are exactly the ones the write endpoint accepts: name,
 * customer number and status. Contacts and addresses already exist on the
 * model and are shown here, but stay read only until the work4all
 * integration (phase 3) owns them - see package scope notes.
 *
 * The write request is authoritative for `customerNumber`, so the form
 * always submits all three fields, never a partial patch.
 */
export function CustomerDetailPage(): ReactNode {
  const { session } = useSession();
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const isNew = customerId === NEW_CUSTOMER_ID;

  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(isNew);

  const [name, setName] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [status, setStatus] = useState<CustomerStatus>('prospect');

  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew || !customerId) {
      return;
    }
    let cancelled = false;
    getCustomer(customerId)
      .then((loadedCustomer) => {
        if (cancelled) {
          return;
        }
        setCustomer(loadedCustomer);
        setName(loadedCustomer.name);
        setCustomerNumber(loadedCustomer.customerNumber ?? '');
        setStatus(loadedCustomer.status);
        setLoaded(true);
      })
      .catch((caught: unknown) => {
        if (cancelled) {
          return;
        }
        setLoadError(
          caught instanceof ApiRequestError ? caught.message : 'Kunde konnte nicht geladen werden.',
        );
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, isNew]);

  if (session?.status !== 'authenticated') {
    return null;
  }
  const { me } = session;
  const canWrite = me.permissions.includes('customers:write');

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveError(undefined);
    setSaving(true);
    try {
      const targetId = isNew ? entityId.customer(crypto.randomUUID()) : customerId!;
      const saved = await saveCustomer(targetId, {
        name,
        customerNumber,
        status,
        ...(customer?._etag ? { ifMatchEtag: customer._etag } : {}),
      });
      if (isNew) {
        navigate(`/kunden/${encodeURIComponent(saved.id)}`, { replace: true });
      } else {
        setCustomer(saved);
        setName(saved.name);
        setCustomerNumber(saved.customerNumber ?? '');
        setStatus(saved.status);
      }
    } catch (caught: unknown) {
      setSaveError(caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {!isNew && customer ? (
          <CustomerContextNav
            customerId={customer.id}
            customerName={customer.name}
            features={me.features}
          />
        ) : null}

        <div className="card">
          <h1>{isNew ? 'Kunde anlegen' : name || 'Kunde'}</h1>

          {loadError ? (
            <p className="notice notice--warning">{loadError}</p>
          ) : !loaded ? (
            <p className="muted" role="status" aria-live="polite">
              Kunde wird geladen ...
            </p>
          ) : (
            <form className="stack" onSubmit={(event) => void handleSubmit(event)}>
              <div className="form-field">
                <label htmlFor="customer-name">Name</label>
                <input
                  id="customer-name"
                  type="text"
                  required
                  disabled={!canWrite}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="customer-number">Kundennummer</label>
                <input
                  id="customer-number"
                  type="text"
                  disabled={!canWrite}
                  value={customerNumber}
                  onChange={(event) => setCustomerNumber(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="customer-status">Status</label>
                <select
                  id="customer-status"
                  disabled={!canWrite}
                  value={status}
                  onChange={(event) => setStatus(event.target.value as CustomerStatus)}
                >
                  {CUSTOMER_STATUSES.map((candidate) => (
                    <option key={candidate} value={candidate}>
                      {CUSTOMER_STATUS_LABELS[candidate]}
                    </option>
                  ))}
                </select>
              </div>

              {saveError ? <p className="notice notice--warning">{saveError}</p> : null}

              {canWrite ? (
                <p>
                  <button className="button button--primary" type="submit" disabled={saving}>
                    {saving ? 'Speichert ...' : 'Speichern'}
                  </button>
                </p>
              ) : null}
            </form>
          )}
        </div>

        {!isNew && customer ? (
          <>
            <div className="card">
              <h2>Adressen</h2>
              {customer.addresses.length === 0 ? (
                <p className="muted">Noch keine Adresse hinterlegt.</p>
              ) : (
                <ul className="stack">
                  {customer.addresses.map((address) => (
                    <li key={address.id}>
                      <span className="badge">{address.label ?? address.kind}</span>{' '}
                      {[address.street, address.postalCode, address.city, address.country]
                        .filter(Boolean)
                        .join(', ')}
                    </li>
                  ))}
                </ul>
              )}
              <p className="muted small">
                Adressen werden ab Phase 3 aus work4all uebernommen und sind hier nicht editierbar.
              </p>
            </div>

            <div className="card">
              <h2>Ansprechpartner</h2>
              {customer.contacts.length === 0 ? (
                <p className="muted">Noch kein Ansprechpartner hinterlegt.</p>
              ) : (
                <ul className="stack">
                  {customer.contacts.map((contact) => (
                    <li key={contact.id}>
                      {contact.displayName}
                      {contact.role ? (
                        <span className="muted"> &middot; {contact.role}</span>
                      ) : null}
                      {contact.email ? (
                        <span className="muted"> &middot; {contact.email}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              <p className="muted small">
                Ansprechpartner werden ab Phase 3 aus work4all uebernommen und sind hier nicht
                editierbar.
              </p>
            </div>
          </>
        ) : null}
      </section>
    </Layout>
  );
}
